import React, { useEffect, useState } from 'react';
import { RouteComponentProps, navigate } from '@reach/router';
import { RFC } from 'state/StateTypes';
import Input from 'components/Form/Input';
import * as userTypes from 'state/user/UserTypes';
import Button from 'components/Button';
import Form from 'components/Form';
import Container from 'components/Container';
import FormContainer from 'components/FormContainer';
import ErrorMessage from 'components/ErrorMessage';
import FormHeader from 'components/FormHeader';
import Legend from 'components/Legend';
import { InputChange, FormSubmission, Error, ErrorCodes } from 'types';
import { createUser } from 'state/user/UserActions';
import { validateEmail, toPascalCase, presentErrorBasedOnCode } from 'utils';
import isAuthenticated from 'hooks/isAuthenticated';
import BackButton from 'components/BackButton';

const initialFormState: userTypes.CreateUserRequest = {
   [userTypes.UserKeys.email]: '', //email
   [userTypes.UserKeys.firstName]: '',
   [userTypes.UserKeys.lastName]: '',
   [userTypes.UserKeys.phone]: '',
};

const AdminCreate: RFC<RouteComponentProps> = () => {
   const [form, setForm] = useState<userTypes.CreateUserRequest>(initialFormState);
   const [error, setError] = useState<Error | null>(null);
   const [createdAdmin, setCreatedAdmin] = useState<userTypes.User | undefined>(undefined);
   const adminRoleRequired = true;
   const authenticated: boolean = isAuthenticated(adminRoleRequired);
   const [loading, setLoading] = useState<boolean>(false);
   const [formComplete, setFormComplete] = useState<boolean>(false);
   const [isValidEmail, setIsValidEmail] = useState<boolean>(false);

   const handleSubmit = async (e: FormSubmission) => {
      e.preventDefault();
      if (authenticated) {
         try {
            setLoading(true);
            const admin = await createUser({
               ...form,
               adminStatus: userTypes.AdminStatus.active,
               firstName: toPascalCase(form.firstName),
               lastName: form.lastName ? toPascalCase(form.lastName) : '',
            });
            setCreatedAdmin(admin);
         } catch (error) {
            //if error results from missing token it will not have a json method
            error = error.json ? await error.json() : error;
            setError(error);
         }
         setLoading(false);
      }
   };

   useEffect(() => {
      if (!!form.email && !!form.firstName && !!form.phone) {
         setIsValidEmail(validateEmail(form.email));
         isValidEmail && setFormComplete(true);
      } else {
         setFormComplete(false);
      }
   }, [form.email, form.firstName, form.phone, isValidEmail]);

   useEffect(() => {
      if (createdAdmin) {
         //navigate user to created admin's details
         navigate(`/admins/${createdAdmin._id}`);
      }
   }, [createdAdmin]);

   const handleInput = (e: InputChange): void => {
      e.persist();
      setForm(form => ({ ...form, [e.target.name]: e.target.value }));
   };

   return (
      <Container margin="0 auto">
         <BackButton to="/admins" />
         <FormContainer>
            <FormHeader>CREATE ADMIN</FormHeader>
            <Legend>
               Fields with <span>*</span> are required
            </Legend>
            <Form onSubmit={handleSubmit}>
               <Input
                  label="Email"
                  value={form.email}
                  name={userTypes.UserKeys.email}
                  onChange={handleInput}
                  //ensure user isn't able to click create button if email is invalid
                  size="full"
                  errorMessage="Email is required"
                  type="email"
                  required={true}
               />
               <Input
                  label="First Name"
                  value={form.firstName}
                  name={userTypes.UserKeys.firstName}
                  onChange={handleInput}
                  size="full"
                  type="text"
                  required={true}
               />
               <Input label="Last Name" value={form.lastName} name={userTypes.UserKeys.lastName} onChange={handleInput} size="full" type="text" />
               <Input
                  label="Phone"
                  value={form.phone}
                  name={userTypes.UserKeys.phone}
                  onChange={handleInput}
                  size="full"
                  type="tel"
                  required={true}
               />
               <Button isLoading={loading} disabled={!formComplete || loading || !isValidEmail}>
                  CREATE
               </Button>
            </Form>
            {error && <ErrorMessage>{presentErrorBasedOnCode(error.code as ErrorCodes)}</ErrorMessage>}
         </FormContainer>
      </Container>
   );
};

export default AdminCreate;
